/* The knowledge base (piece 30): the list and its search, an article's page with
   its body, tags, quiz tile and versions, the editor in place of the body, the
   quiz a person sits, and the category tree with its tiers. */
.page,
.column,
.fields,
.section {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

/*
 * 🔒 The article page (T416; decision 1100): the article stands in one white
 * tile beside its panels, and the tile takes the height the page leaves under
 * the title bar, so its middle scrolls on its own and the panels stay put.
 * 🔨 The Content region is the frame's one scroller and has a definite height,
 * so this page fills it with `height: 100%` rather than measuring the screen.
 */
.reader {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  /* On a phone the tile is the page's height and the panels follow beneath it. */
  grid-template-rows: 100% auto;
  gap: var(--space-4);
  height: 100%;
  min-width: 0;
}

.articleSide {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  /* The Content region's own padding does not follow content that overflows it, so the panels carry the page's foot. */
  padding-bottom: var(--space-3);
}

@media (--bp-md) {
  .reader {
    grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr);
  }

  /* The panels scroll themselves only when they are taller than the page. */
  .articleSide {
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 0;
  }
}

/* 🔨 While the editor is open the tile grows with it and the page scrolls: a menu inside a scrolling box is cut off at its edge. */
.reader[data-article-mode='editing'] {
  grid-template-rows: auto auto;
  height: auto;
}

@media (--bp-md) {
  .reader[data-article-mode='editing'] {
    grid-template-rows: auto;
    align-items: start;
  }
}

.articleTile {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--tile-border);
  border-radius: var(--radius-medium);
  background: var(--surface);
  box-shadow: var(--tile-shadow);
}

.articleTileHead {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--tile-border);
}

.articleTileBody {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-3) var(--space-4);
}

.reader[data-article-mode='editing'] .articleTileBody {
  overflow: visible;
}

/* 🔨 The tile clips its corners without becoming a scroll box of its own, or the editor's toolbar would stick to the tile and scroll away with it (T555; decision 1273). */
.reader[data-article-mode='editing'] .articleTile {
  overflow: clip;
}

.articleTileBody:focus-visible {
  outline: var(--focus-ring);
  outline-offset: calc(var(--focus-ring-offset) * -1);
}

.articleTileFoot {
  flex: none;
  padding: var(--space-2) var(--space-4);
  border-top: 1px solid var(--tile-border);
  background: var(--surface-2);
  color: var(--text-muted);
  font-size: var(--font-size-small);
}

/* The draft read as a reader would (T373; decision 1101): its own words under the notice that marks it. */
.draftBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.editor {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-4);
  align-items: start;
}

@media (--bp-md) {
  .layout {
    grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  }
}

/* Decision 2281: the Articles, Categories and Checklists tabs, above each page's own toolbar. */
.tabs {
  display: flex;
  min-width: 0;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-2);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.hint,
.meta {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--font-size-small);
}

.problem {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-medium);
  background: var(--status-error-bg);
  color: var(--status-error-text);
}

.notice {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-medium);
  background: var(--status-warning-bg);
  color: var(--status-warning-text);
}

.crumbs {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--font-size-small);
  color: var(--text-muted);
}

.blockTitle {
  margin: 0;
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-bold);
}

.cardTitleRow {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

/* The article body: the one stored content, drawn as it will print. */
.body {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  line-height: var(--line-height-base);
  min-width: 0;
}

/*
 * 🔒 The reading view (T3654; decision 11160): the article's own words read the
 * reading tokens, a serif face at the reading size with a looser line, a measure
 * of about seventy characters and air between blocks and list items. Only the
 * words the body draws carry the class; its table of contents, the editor, the
 * comments and every other screen keep the base face. A heading stands above
 * the words in bold at the article PDF's own step, and its air at least the
 * PDF's (the owner, 2026-10-31: "The pdf spacing is better"); code keeps
 * the fixed-width face the shared view gives it, and a callout its own frame.
 * The words' rule carries the view's attribute so it outweighs the shared
 * view's own article size, whichever stylesheet loads last.
 */
.body .readingView[data-reading-view='true'] {
  max-width: var(--measure-reading);
  font-family: var(--font-family-reading);
  font-size: var(--font-size-reading);
  line-height: var(--line-height-reading);
}

.body .readingView :is(p, ul, ol, table, blockquote, pre) {
  margin: 0 0 var(--space-reading-block);
}

/* A callout keeps its frame and takes the same air after it as any block, as the PDF leaves under it. */
.body .readingView aside[data-kb-callout] {
  margin-bottom: var(--space-reading-block);
}

/*
 * A block inside a list item, a cell or a quotation keeps the gap between its own blocks, and its last one gives its air up to the
 * container, so a nested paragraph adds no block gap under the item and a quotation's end does not double (T3661).
 */
.body .readingView :is(li, td, th, blockquote) > :last-child {
  margin-bottom: 0;
}

.body .readingView li + li,
.body .readingView li > :is(ul, ol) {
  margin-top: var(--space-reading-item);
}

.body .readingView li > :is(ul, ol) {
  margin-bottom: 0;
}

.body .readingView :is(h1, h2, h3) {
  margin: var(--space-5) 0 var(--space-3);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-tight);
}

.body .readingView h1 {
  font-size: var(--reading-heading-1-size);
}

.body .readingView h2 {
  font-size: var(--reading-heading-2-size);
}

.body .readingView h3 {
  font-size: var(--reading-heading-3-size);
}

.body .readingView > :first-child {
  margin-top: 0;
}

.body .readingView > :last-child {
  margin-bottom: 0;
}

/* The words after the table of contents are their own segment, so their first heading takes the heading's air back (T3661). */
.body .toc + .readingView > :is(h1, h2, h3):first-child {
  margin-top: var(--space-5);
}

/* 🔒 A reader's picture opens at full size on a press (T735; decision 1476); a picture inside a link stays the link's. */
.body [data-kb-zoom] {
  cursor: zoom-in;
}

.body [data-kb-zoom]:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.toc {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--tile-border);
  border-radius: var(--radius-medium);
  background: var(--surface-2);
}

.tocTitle {
  margin: 0;
  font-weight: var(--font-weight-bold);
}

.tocList {
  margin: 0;
  padding-left: var(--space-4);
}

.tocLevel2 {
  margin-left: var(--space-4);
}

.tocLink {
  padding: 0;
  border: none;
  background: none;
  color: var(--navy-primary);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.tocLink:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.passage {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--font-size-small);
}

.passage mark {
  background: var(--selection-bg);
  color: inherit;
}

/* A quiz question and its answers. */
.question {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  border: 1px solid var(--tile-border);
  border-radius: var(--radius-medium);
  background: var(--surface);
}

.questionWrong {
  border-color: var(--status-error-text);
}

.questionPrompt {
  margin: 0;
  font-weight: var(--font-weight-bold);
}

.answerRow {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-2);
}

.statRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  font-size: var(--font-size-small);
}

.bar {
  display: block;
  height: var(--space-2);
  border-radius: var(--radius-small);
  background: var(--status-error-text);
}

.barTrack {
  display: block;
  width: 100%;
  border-radius: var(--radius-small);
  background: var(--surface-2);
}

/* The list's Training pill is a link to the article's Required Quiz Tile (T297; decision 0801). */
.pillLink {
  display: inline-flex;
  border-radius: var(--radius-small);
  text-decoration: none;
}

.pillLink:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* Import a file as an article (T339): the native file picker over its label,
   and the reading state with an indeterminate progress bar beside its words. */
.picker {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.pickerLabel {
  font-size: var(--control-label-size);
  color: var(--text);
}

.reading {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.progress {
  flex: 0 0 auto;
}

/* The import's questions (T386; decision 1014): the tiles on one side and the
   passage asked about on the other, stacked on a phone; each tile a native radio
   drawn as a card, the picked one on the selection wash with a navy edge. */
.askBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.ask {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-4);
  align-items: start;
}

@media (--bp-md) {
  .ask {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}

.tiles {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  border: none;
  min-width: 0;
}

.askTitle {
  margin: 0 0 var(--space-2);
  padding: 0;
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-bold);
  color: var(--text);
}

.tile {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3);
  border: 1px solid var(--tile-border);
  border-radius: var(--radius-medium);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
}

.tile:hover {
  background: var(--surface-2);
}

.tile:focus-within {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.tileChecked,
.tileChecked:hover {
  border-color: var(--navy-primary);
  background: var(--selection-bg);
  font-weight: var(--font-weight-bold);
}

.tileRadio {
  flex: 0 0 auto;
  accent-color: var(--navy-primary);
}

.passage {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: var(--space-3);
  border-radius: var(--radius-medium);
  background: var(--surface-2);
  min-width: 0;
}

.passageLine {
  margin: 0;
  overflow-wrap: anywhere;
}

.passagePicture {
  margin: 0;
  padding: var(--space-2);
  border: 1px dashed var(--tile-border);
  border-radius: var(--radius-small);
  color: var(--text-muted);
  text-align: center;
}

.reportList {
  margin: 0;
  padding-left: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

/* 🔒 A comment's words under its author (decision 9580): the row's own type, wrapping a long word rather than widening the panel. */
.commentBody {
  color: var(--text);
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

/* A formatted comment's paragraphs take no margin of their own, so its time sits as close under it as a plain one's. */
.commentBody p {
  margin: 0;
}
